import { addElement, animate, nap, px, spring, stageSize, type DemoImpl, type Point } from '@/components/motion/demos/demoKit'

/* The firm card's week: routine work (chasing documents, preparing) drops into Justine, and the
   team's freed slots fill with review and client time. */

type Task = 'collect' | 'prep' | 'review' | 'advise'

/** Monday to Friday, four slots each. */
const week: Task[][] = [
  ['collect', 'prep', 'prep', 'advise'],
  ['collect', 'prep', 'review', 'prep'],
  ['prep', 'collect', 'prep', 'review'],
  ['collect', 'prep', 'review', 'advise'],
  ['prep', 'prep', 'review', 'collect'],
]
const taskLabels: Record<Task, string> = { collect: 'Chase docs', prep: 'Prepare', review: 'Review', advise: 'Client time' }

type Cell = { element: HTMLElement; day: number; row: number }
type RoutineCell = Cell & { slot: number }

type WeekContext = {
  stage: HTMLElement
  heads: HTMLElement[]
  rowLabel: HTMLElement
  laneLabel: HTMLElement
  lane: HTMLElement
  blocks: Cell[]
  fills: Cell[]
  routine: RoutineCell[]
  pills: HTMLElement[]
}

const pillHeight = 18

const geometry = (context: WeekContext) => {
  const { width } = stageSize(context.stage)
  const x0 = 60
  const x1 = width
  const top = 24
  const rowHeight = 38
  const gap = 5
  const columnWidth = (x1 - x0 - gap * 4) / 5
  const laneY = top + 4 * (rowHeight + gap) + 12
  const count = context.routine.length
  const pillWidth = (x1 - x0 - 8 - (count - 1) * 3) / count
  return {
    x0,
    x1,
    gap,
    rowHeight,
    columnWidth,
    laneY,
    pillWidth,
    cell: (day: number, row: number): Point => [x0 + day * (columnWidth + gap), top + row * (rowHeight + gap)],
    pill: (slot: number): Point => [x0 + 4 + slot * (pillWidth + 3), laneY + 5],
  }
}

type WeekGeometry = ReturnType<typeof geometry>

const put = (element: HTMLElement, [x, y]: Point, width: number, height: number) =>
  Object.assign(element.style, { left: px(x), top: px(y), width: px(width), height: px(height) })

const layout = (context: WeekContext, g: WeekGeometry) => {
  context.heads.forEach((head, day) => {
    head.style.left = px(g.x0 + day * (g.columnWidth + g.gap) + g.columnWidth / 2)
    head.style.top = '6px'
  })
  context.rowLabel.style.top = px(g.cell(0, 0)[1] + 4)
  Object.assign(context.lane.style, { top: px(g.laneY), left: px(g.x0), width: px(g.x1 - g.x0) })
  context.laneLabel.style.top = px(g.laneY + 7)
  for (const cell of [...context.blocks, ...context.fills]) put(cell.element, g.cell(cell.day, cell.row), g.columnWidth, g.rowHeight)
  context.pills.forEach((pill, slot) => put(pill, g.pill(slot), g.pillWidth, pillHeight))
}

const isRoutine = (task: Task) => task === 'collect' || task === 'prep'

export const weekDemo: DemoImpl<WeekContext> = {
  setup: (stage) => {
    const heads = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day) => addElement('span', 'm-week-head', stage, day))
    const rowLabel = addElement('span', 'm-rowlab', stage, 'Your team')
    const laneLabel = addElement('span', 'm-rowlab', stage, 'Justine')
    const lane = addElement('div', 'm-lane', stage)
    const blocks: Cell[] = []
    const fills: Cell[] = []
    const routine: RoutineCell[] = []
    week.forEach((tasks, day) =>
      tasks.forEach((task, row) => {
        const block = { element: addElement('div', `m-blk ${task}`, stage, taskLabels[task]), day, row }
        blocks.push(block)
        if (!isRoutine(task)) return
        // Freed slots alternate between client time and review.
        const fillTask: Task = routine.length % 2 === 1 ? 'review' : 'advise'
        fills.push({ element: addElement('div', `m-blk ${fillTask}`, stage, taskLabels[fillTask]), day, row })
        routine.push({ ...block, slot: routine.length })
      }),
    )
    const pills = routine.map(() => addElement('div', 'm-blk os', stage, ''))
    return { stage, heads, rowLabel, laneLabel, lane, blocks, fills, routine, pills }
  },
  reset: (context) => {
    layout(context, geometry(context))
    context.blocks.forEach((block) => {
      block.element.style.opacity = '1'
    })
    context.fills.forEach((fill) => {
      fill.element.style.opacity = '0'
    })
    context.pills.forEach((pill) => {
      pill.style.opacity = '0'
    })
  },
  final: (context) => {
    layout(context, geometry(context))
    const routineElements = new Set(context.routine.map((cell) => cell.element))
    context.blocks.forEach((block) => {
      block.element.style.opacity = routineElements.has(block.element) ? '0' : '1'
    })
    context.fills.forEach((fill) => {
      fill.element.style.opacity = '1'
    })
    context.pills.forEach((pill) => {
      pill.style.opacity = '1'
    })
  },
  play: async (context, ok) => {
    const g = geometry(context)
    const lifted = 'translateY(-3px) scale(1.04)'
    const liftShadow = '0 10px 22px rgba(3,7,44,.18)'
    context.routine.forEach((cell, index) =>
      animate(
        cell.element,
        [
          { transform: 'none', boxShadow: '0 0 0 rgba(3,7,44,0)', transformOrigin: 'top left' },
          { transform: lifted, boxShadow: liftShadow, transformOrigin: 'top left' },
        ],
        { duration: 240, delay: index * 35 },
      ),
    )
    await nap(240 + context.routine.length * 35 + 200)
    ok()
    context.routine.forEach((cell, index) => {
      const [bx, by] = g.cell(cell.day, cell.row)
      const [pillX, pillY] = g.pill(cell.slot)
      const style = getComputedStyle(cell.element)
      animate(
        cell.element,
        [
          { transform: lifted, backgroundColor: style.backgroundColor, color: style.color, transformOrigin: 'top left', boxShadow: liftShadow },
          {
            transform: `translate(${pillX - bx}px, ${pillY - by}px) scale(${g.pillWidth / g.columnWidth}, ${pillHeight / g.rowHeight})`,
            backgroundColor: '#2A55E0',
            color: 'rgba(255,255,255,0)',
            transformOrigin: 'top left',
            boxShadow: '0 0 0 rgba(0,0,0,0)',
          },
        ],
        { duration: 760, delay: index * 55, easing: 'cubic-bezier(.6,0,.2,1)' },
      )
    })
    await nap(760 + context.routine.length * 55)
    ok()
    context.pills.forEach((pill) => {
      pill.style.opacity = '1'
    })
    context.routine.forEach((cell) => {
      cell.element.getAnimations().forEach((animation) => animation.cancel())
      cell.element.style.opacity = '0'
    })
    context.fills.forEach((fill, index) => {
      fill.element.style.opacity = '1'
      animate(
        fill.element,
        [
          { transform: 'scaleY(0)', transformOrigin: 'bottom', opacity: 0.4 },
          { transform: 'scaleY(1)', transformOrigin: 'bottom', opacity: 1 },
        ],
        { duration: 480, delay: 100 + index * 55, easing: spring },
      )
    })
  },
}
